import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Stethoscope, 
  User, 
  Lock, 
  Key, 
  X, 
  Eye, 
  EyeOff, 
  AlertTriangle,
  ArrowRight,
  ShieldAlert
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import './StaffLogin.css';

const StaffLogin = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [validating, setValidating] = useState(false);
  
  const navigate = useNavigate();

  async function fetchStaff() {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_public_professionals');
      
      if (error) throw error;
      
      // Filter out hidden ones
      setStaffList(data?.filter(item => !item.is_hidden) || []);
    } catch (err) {
      console.error('Error fetching staff list:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Clear any previous session
    localStorage.removeItem('gdt_active_staff');
    localStorage.removeItem('gdt_active_user');
    Promise.resolve().then(() => {
      fetchStaff();
    });
  }, []);

  const handleSelectStaff = (staff) => {
    setSelectedStaff(staff);
    setErrorMsg('');
    setPassword('');
    // Auto-select first user if available
    if (staff.users && staff.users.length > 0) {
      setSelectedUser(staff.users[0].username);
    } else {
      setSelectedUser('');
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;
    
    setValidating(true);
    setErrorMsg('');

    const matchedUser = selectedStaff.users?.find(u => u.username?.toLowerCase() === selectedUser?.toLowerCase()) || { username: selectedUser, role: 'Especialista' };

    // Función para guardar sesión en localStorage Y sessionStorage (cross-browser)
    const saveSession = (staff, user) => {
      const staffStr = JSON.stringify(staff);
      const userStr = JSON.stringify(user);
      localStorage.setItem('gdt_active_staff', staffStr);
      localStorage.setItem('gdt_active_user', userStr);
      sessionStorage.setItem('gdt_active_staff', staffStr);
      sessionStorage.setItem('gdt_active_user', userStr);
    };

    // Intento 1: Validar vía Supabase Auth (funciona en cualquier navegador/computadora)
    const email = `${selectedUser.toLowerCase()}@gestionamientodeturnos.com`;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (!error && data?.session) {
        saveSession(selectedStaff, matchedUser);
        setTimeout(() => {
          setValidating(false);
          navigate('/dashboard');
        }, 800);
        return;
      }
    } catch (_) {
      // Si falla Supabase Auth (p.ej. usuario no creado en Auth), caemos al backdoor local
    }

    // Intento 2: Backdoor Seguro vía Supabase RPC (No expone contraseñas al cliente)
    try {
      const { data, error } = await supabase.rpc('verify_staff_login', {
        p_username: selectedUser,
        p_password: password
      });

      if (!error && data && data.staff && data.user) {
        saveSession(data.staff, data.user);
        setTimeout(() => {
          setValidating(false);
          navigate('/dashboard');
        }, 800);
        return;
      }
    } catch (err) {
      console.error(err);
    }

    // Ninguno funcionó
    setErrorMsg('Credenciales incorrectas. Verifique su contraseña.');
    setValidating(false);
  };

  const getCardIcon = (staff) => {
    if (staff.full_name?.toLowerCase().includes('administracion') || staff.type?.toLowerCase().includes('administrador') || staff.type?.toLowerCase().includes('secretaria')) {
      return (
        <div className="card-icon-circle admin">
          <User size={24} />
        </div>
      );
    }
    return (
      <div className="card-icon-circle pro">
        <Stethoscope size={24} />
      </div>
    );
  };

  const getSubLabel = (staff) => {
    if (staff.full_name?.toLowerCase().includes('otorrino')) {
      return 'STAFF ADMINISTRADOR';
    }
    if (staff.full_name?.toLowerCase().includes('administracion')) {
      return 'ÁREA GENERAL';
    }
    return staff.specialty || staff.type || 'ESPECIALISTA';
  };

  return (
    <div className="staff-login-page">
      <div className="login-background-glow">
        <div className="glow-sphere cyan"></div>
        <div className="glow-sphere blue"></div>
      </div>

      <div className="login-container-wrapper">
        <div className="login-logo-header">
          <div className="login-logo-circle">
            <span className="logo-pulse"></span>
          </div>
          <h2>GDT</h2>
        </div>

        <h1 className="login-title">Selecciona Tu Usuario</h1>
        <p className="login-subtitle">
          Si olvidaste la contraseña de acceso, podrás resetearla en el paso siguiente.
        </p>
        <p className="login-subtitle highlight">
          Se te solicitará la contraseña al seleccionar el cuadro correspondiente a tu Staff
        </p>

        {loading ? (
          <div className="loading-container">
            <div className="loader-ring"><div></div><div></div><div></div><div></div></div>
            <p>Cargando áreas de staff...</p>
          </div>
        ) : (
          <div className="staff-cards-grid">
            {staffList.map((staff) => (
              <div 
                key={staff.id} 
                className={`staff-card-button ${selectedStaff?.id === staff.id ? 'active' : ''}`}
                onClick={() => handleSelectStaff(staff)}
              >
                {getCardIcon(staff)}
                <div className="staff-card-text">
                  <h3>{staff.full_name}</h3>
                  <span className="staff-card-sub">{getSubLabel(staff)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* POPUP MODAL PARA CONTRASEÑA */}
      {selectedStaff && (
        <div className="login-modal-overlay" onClick={() => setSelectedStaff(null)}>
          <div className="login-modal-card glassmorphic" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-v4">
              <div className="header-icon-title">
                {selectedStaff.full_name?.toLowerCase().includes('administracion') ? (
                  <div className="modal-title-circle admin"><User size={18} /></div>
                ) : (
                  <div className="modal-title-circle pro"><Stethoscope size={18} /></div>
                )}
                <span>Acceso Seguro a {selectedStaff.full_name}</span>
              </div>
              <button className="btn-close-modal-v4" onClick={() => setSelectedStaff(null)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleLoginSubmit} className="login-modal-form">
              <div className="form-group-v4">
                <label>Selecciona tu Usuario</label>
                {selectedStaff.users && selectedStaff.users.length > 0 ? (
                  <select 
                    value={selectedUser} 
                    onChange={(e) => setSelectedUser(e.target.value)}
                    required
                  >
                    {selectedStaff.users.map((u, i) => (
                      <option key={i} value={u.username}>{u.username.toUpperCase()} ({u.role})</option>
                    ))}
                  </select>
                ) : (
                  <input 
                    type="text" 
                    value={selectedUser} 
                    onChange={(e) => setSelectedUser(e.target.value)} 
                    placeholder="Nombre de usuario" 
                    required 
                  />
                )}
              </div>

              <div className="form-group-v4">
                <label>Contraseña de Acceso</label>
                <div className="password-input-wrapper-v4">
                  <Lock size={16} className="pass-icon" />
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button 
                    type="button" 
                    className="btn-toggle-eye" 
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {errorMsg && (
                <div className="login-error-alert">
                  <AlertTriangle size={16} />
                  <span>{errorMsg}</span>
                </div>
              )}

              <div className="login-modal-footer">
                <button 
                  type="button" 
                  className="btn-modal-cancel" 
                  onClick={() => setSelectedStaff(null)}
                >
                  CANCELAR
                </button>
                <button 
                  type="submit" 
                  className="btn-modal-submit"
                  disabled={validating}
                >
                  {validating ? 'ACCEDIENDO...' : 'INGRESAR AL ÁREA'}
                  {!validating && <ArrowRight size={16} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffLogin;
